import DOMPurify from 'dompurify';

/**
 * Allowlist sanitisation for the note fields this app renders as HTML.
 *
 * ── Why this exists on the client as well as the server ──────────────────────
 *
 * The backend sanitises these fields on write (see HtmlSanitiser.java), and that
 * is the authoritative fix. This is the second half, for two reasons:
 *
 *   1. Rows written BEFORE the server-side fix are still in the database exactly
 *      as they were submitted. Server-side sanitisation is applied on write, so
 *      it does nothing for a payload that is already stored — and those rows are
 *      rendered by the same three components.
 *
 *   2. Three components render note text with dangerouslySetInnerHTML whenever
 *      the string happens to start with '<'. If a fourth appears, or a new write
 *      path is added server-side without sanitising, this is the backstop.
 *
 * Access and refresh tokens both live in localStorage and there is no CSP on any
 * response, so script running here reads them directly. Until both of those are
 * fixed, a single missed render site is account takeover.
 *
 * ── Keep this list in step with the server ──────────────────────────────────
 *
 * The tags below mirror HtmlSanitiser.NOTE_SAFELIST. If you widen one, widen the
 * other, or content that saves cleanly will render stripped and look like data
 * loss to the user.
 */
const ALLOWED_TAGS = [
  // ⚠️ '#text' IS LOAD BEARING. When ALLOWED_TAGS is given explicitly, DOMPurify
  // treats text nodes as just another node type and drops them unless they are
  // listed — so omitting it turns "<b>ok</b>" into "<b></b>" and silently blanks
  // every note in the app while still passing a "no script survived" assertion.
  '#text',
  'b', 'strong', 'i', 'em', 'u', 's', 'strike',
  'p', 'br', 'div', 'span',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'code', 'pre',
];

/**
 * No attributes at all — matching the server. An attribute allowlist is the part
 * that usually leaks: a permitted `style` or `href` is a fresh injection point,
 * and nothing in the note editor produces either.
 */
const ALLOWED_ATTR = [];

/**
 * Sanitise a note for rendering through dangerouslySetInnerHTML.
 *
 * @param {string|null|undefined} html untrusted note content
 * @returns {string} sanitised HTML, always a string so it is safe to spread into
 *                   dangerouslySetInnerHTML without a null check at the call site
 */
export function sanitiseNoteHtml(html) {
  if (html === null || html === undefined) return '';
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Drop the contents of removed elements rather than promoting them to text.
    // Without this, <script>alert(1)</script> renders as the literal string
    // "alert(1)", which is not dangerous but is visible nonsense in a note.
    KEEP_CONTENT: false,
  });
}

/**
 * Build the prop object for dangerouslySetInnerHTML in one step, so a call site
 * cannot accidentally pass raw content.
 *
 * Prefer this over calling sanitiseNoteHtml yourself — grepping for
 * `dangerouslySetInnerHTML={{ __html:` should find no raw values.
 *
 * @param {string|null|undefined} html untrusted note content
 * @returns {{__html: string}}
 */
export function safeNoteHtml(html) {
  return { __html: sanitiseNoteHtml(html) };
}
